/**
 * React Query hook for Time Entries List V2 with RDS-based pagination
 * Uses the new listTimeEntriesV2 API with improved filtering and sorting
 */

import { useQuery } from '@tanstack/react-query';
import { listTimeEntriesV2 } from 'services/timerServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches time entries data with RDS-based pagination
 * API Response format: { total_count, time_entries: [], page_count, page, limit }
 *
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} - Response with data array and pagination metadata
 */
const fetchTimeEntriesV2 = async ({
  limit = 30,
  page = 1,
  billing_status = null,
  invoice_status = '',
  time_entry_type = 'manual',
  practice_area = [],
  time_entry_from = '',
  time_entry_to = '',
  firm_user_id = [],
  sort_by = '',
  sort_order = '',
  search_key = '',
  case_id = '',
  full_list = false,
}) => {
  try {
    const response = await listTimeEntriesV2({
      limit,
      page,
      billing_status,
      invoice_status,
      time_entry_type,
      practice_area,
      time_entry_from,
      time_entry_to,
      firm_user_id,
      sort_by,
      sort_order,
      search_key,
      case_id,
      full_list,
    });

    if (response?.data) {
      // Transform API response to match previous implementation structure
      const transformedData = {
        ...response.data,
        time_entries: (response.data.data || []).map((entry) => {
          // Parse team_member_user_name to extract first, middle, last name
          const nameParts = entry.team_member_user_name?.trim().split(/\s+/) || [];
          const firstName = nameParts[0] || '';
          const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : nameParts[1] || '';
          const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

          return {
            ...entry,
            // Transform team_member fields to created_by_details structure (matching previous implementation)
            created_by_details: {
              user_id: entry.team_member_firm_user_id,
              first_name: firstName,
              middle_name: middleName,
              last_name: lastName,
            },
          };
        }),
      };
      return transformedData;
    }

    // Return empty structure if no data (matching previous implementation format)
    return {
      total_count: 0,
      time_entries: [],
      page_count: 0,
      page: page,
      limit: limit,
    };
  } catch (error) {
    console.error('Error fetching time entries V2:', error);
    throw error;
  }
};

/**
 * Hook to fetch time entries with RDS-based pagination using React Query
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Items per page (default: 30)
 * @param {number} options.page - Page number (default: 1)
 * @param {boolean|null} options.billing_status - Filter by billing status (null for All, true for Billable, false for Non-Billable)
 * @param {string} options.invoice_status - Filter by invoice status ('' for All, 'INVOICED', 'OPEN')
 * @param {string} options.time_entry_type - Time entry type: 'automated' or 'manual' (default: 'manual')
 * @param {Array} options.practice_area - Filter by practice area IDs
 * @param {string} options.time_entry_from - Start date filter (ISO format with timezone)
 * @param {string} options.time_entry_to - End date filter (ISO format with timezone)
 * @param {Array} options.firm_user_id - Filter by firm user IDs
 * @param {string} options.sort_by - Field to sort by (default: '')
 * @param {string} options.sort_order - Sort order: 'asc' or 'desc' (default: '')
 * @param {string} options.search_key - Search keyword for filtering time entries
 * @param {string} options.case_id - Filter by case ID
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} Query result object
 * @returns {Array} returns.timeEntries - Array of time entry objects
 * @returns {number} returns.totalRecords - Total number of records
 * @returns {number} returns.currentPage - Current page number
 * @returns {number} returns.totalPages - Total number of pages
 * @returns {number} returns.pageSize - Items per page
 * @returns {boolean} returns.isLoading - Initial loading state
 * @returns {boolean} returns.isFetching - Fetching state (including background refetch)
 * @returns {boolean} returns.isError - Error state indicator
 * @returns {Object|null} returns.error - Error object if request failed
 * @returns {Function} returns.refetch - Function to manually refetch data
 *
 * @example
 * const {
 *   timeEntries,
 *   totalRecords,
 *   totalPages,
 *   isLoading,
 *   refetch
 * } = useTimeEntriesListV2Query({
 *   limit: 30,
 *   page: 1,
 *   time_entry_type: 'automated',
 *   invoice_status: 'OPEN',
 *   sort_by: 'time_entry_date',
 *   sort_order: 'desc',
 * });
 */
export const useTimeEntriesListV2Query = ({
  limit = 30,
  page = 1,
  billing_status = null,
  invoice_status = '',
  time_entry_type = 'manual',
  practice_area = [],
  time_entry_from = '',
  time_entry_to = '',
  firm_user_id = [],
  sort_by = '',
  sort_order = '',
  search_key = '',
  case_id = '',
  full_list = false,
  enabled = true,
} = {}) => {
  // Create stable query key based on all filter parameters
  const queryKey = queryKeys.timeEntries.listV2(
    {
      billing_status,
      invoice_status,
      time_entry_type,
      practice_area,
      time_entry_from,
      time_entry_to,
      firm_user_id,
      sort_by,
      sort_order,
      search_key,
      case_id,
      full_list,
    },
    page,
    limit
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchTimeEntriesV2({
        limit,
        page,
        billing_status,
        invoice_status,
        time_entry_type,
        practice_area,
        time_entry_from,
        time_entry_to,
        firm_user_id,
        sort_by,
        sort_order,
        search_key,
        case_id,
        full_list,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes - serves from cache before that
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (React Query v4 uses gcTime instead of cacheTime)
    keepPreviousData: true, // Keep previous data while fetching
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: enabled, // Only refetch on mount if query is enabled
    retry: (failureCount) => {
      // Retry twice for errors
      return failureCount < 2;
    },
  });

  return {
    timeEntries: data?.time_entries || [],
    totalRecords: data?.total_count || 0,
    currentPage: data?.page || page,
    totalPages: data?.page_count || 0,
    pageSize: data?.limit || limit,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export default useTimeEntriesListV2Query;
